import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

// Slim bottom bar that appears once the main Add-to-Cart button scrolls out of view.
export function StickyBuyBar({ product, anchorRef, quantity = 1, soldOut = false, onSoldOut }) {
  const { addToCart, isCartOpen } = useCart();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = anchorRef?.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setShow(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [anchorRef]);

  const visible = show && !isCartOpen && !!product;
  useEffect(() => {
    if (visible) document.body.dataset.buyBar = '1';
    else delete document.body.dataset.buyBar;
    return () => { delete document.body.dataset.buyBar; };
  }, [visible]);

  if (!product) return null;
  const img = (product.images || []).find((i) => !i.endsWith('.mp4')) || product.images?.[0];
  const price = product.priceINR * quantity;

  // Portal: parents use CSS transforms (reveal animations), which would trap position:fixed.
  return createPortal(
    <div
      className={`fixed inset-x-0 bottom-0 z-40 lg:hidden transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full pointer-events-none'}`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="sticky-buy-bar"
      aria-hidden={!visible}
    >
      <div className="mx-3 mb-3 rounded-2xl bg-[#111214]/95 backdrop-blur-md border border-white/10 shadow-[0_-8px_30px_rgba(0,0,0,0.25)] px-3 py-2.5 flex items-center gap-3">
        {img && <img src={img} alt="" className="w-10 h-10 rounded-lg object-contain bg-white/5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold truncate">{product.name}</p>
          <p className="text-zinc-400 text-xs font-mono whitespace-nowrap" data-testid="sticky-buy-price">
            ₹{price.toLocaleString('en-IN')} <span className="text-zinc-500">· free shipping</span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => (soldOut ? onSoldOut?.() : addToCart(product, quantity))}
          className="shrink-0 h-11 px-5 rounded-full bg-[#E02128] hover:bg-[#B81A20] active:scale-[0.97] text-white text-xs font-bold uppercase tracking-[0.12em] inline-flex items-center gap-2 transition-all"
          data-testid="sticky-buy-add"
        >
          <ShoppingCart className="w-4 h-4" />
          {soldOut ? 'Notify me' : 'Add to Cart'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
